import { useCallback, useMemo } from "react";
import type { RepeaterEntry } from "../types/domain";
import {
  parseQ8RepeaterKeywords,
  Q8_REPEATER_ID,
  parseQ7RepeaterKeywords,
  Q7_REPEATER_ID,
} from "../utils/repeaterKeywords";
import type { AxiosError } from "axios";
import { axiosClient, axiosClientGeneratePacket } from "./AxiosClient";
import {
  buildPersonalDemographicFormMapper,
  buildResidentialAddressFormMapper,
  buildLicenseEmergencyContactFormMapper,
  buildUniversalCaresDonationFormMapper,
  buildIndividualResidentLicenseApplicationCriminalHistoryMapper,
  buildIndividualResidentLicenseApplicationLicenseRegistrationIssuesMapper,
  buildIndividualResidentLicenseApplicationJudgmentsBankruptcyMapper,
  buildIndividualResidentLicenseApplicationDelinquentTaxesMapper,
  buildIndividualResidentLicenseApplicationLawsuitsArbitrationMapper,
  buildIndividualResidentLicenseApplicationChildSupportObligationsMapper,
  buildIndividualResidentLicenseApplicationLicensingOtherStatesMapper,
  buildIndividualResidentLicenseApplicationCompanyOwnershipMapper,
  buildIndividualResidentLicenseApplicationUnclaimedPropertyMapper,
} from "../data/onbaseKeywordMapper";
import {
  serializeMinnesotaFileMap,
  buildMinnesotaFileMapFromStored,
  mergeMinnesotaFileMaps,
  enrichMinnesotaFilesWithMetroConfig,
  MinnesotaAttachmentFileMap,
} from "../components/agentForm/minnesotaQuestions/attachmentConfig";
import { clearStaleCaches, getUserData } from "../services/userData";

type ApiResponse<T = unknown> = {
  data?: T;
  message?: string;
  success?: boolean;
};

// (Removed unused MetroOption interface to satisfy no-unused-vars lint rule)

export interface MetroContactInfo {
  metroId: string;
  brandKey: string;
  supportEmail: string;
  metroName: string;
  supportbranchmanager?: string;
}

interface PacketGenerationStatus {
  agentStatus?: string;
  [key: string]: unknown;
}

// Use an Error subclass so rejections are proper Error instances (Sonar: reject with Error)
class ApiRequestError extends Error {
  errors?: unknown;
  status?: number;
  constructor(
    message?: string,
    options?: { errors?: unknown; status?: number }
  ) {
    super(message ?? "Request failed");
    this.name = "ApiRequestError";
    Object.setPrototypeOf(this, new.target.prototype);
    this.errors = options?.errors;
    this.status = options?.status;
  }
}

// Cache the IV parameter for the lifetime of the session to avoid repeated lookups
let __cachedIVParam: string | undefined;

const toApiRequestError = (e: unknown): ApiRequestError => {
  const ax = e as AxiosError<unknown> | undefined;
  const respData = ax?.response?.data;
  let message: string | undefined;
  let errors: unknown;

  if (respData && typeof respData === "object") {
    const obj = respData as Record<string, unknown>;
    const m = obj["message"];
    if (typeof m === "string") message = m;

    const errs = obj["errors"];
    if (errs && typeof errs === "object") {
      const inner = (errs as Record<string, unknown>)["error"];
      errors = inner ?? errs;
    } else {
      errors = errs;
    }
  }

  message = message ?? ax?.message ?? "Request failed";
  const status = ax?.response?.status;
  return new ApiRequestError(message, { errors, status });
};

const extractIVFromLocation = (): string | undefined => {
  // Fast path: return cached value if already resolved this session
  if (__cachedIVParam) return __cachedIVParam;

  // Helper to read from persisted user-data
  const readFromStorage = (): string | undefined => {
    try {
      const raw =
        typeof localStorage !== "undefined"
          ? localStorage.getItem("user-data")
          : null;
      if (!raw) return undefined;
      const parsed = JSON.parse(raw);
      const stored = parsed?.iv ?? parsed?.IV; // support either casing if previously stored
      return typeof stored === "string" && stored ? stored : undefined;
    } catch {
      // Swallow storage parse errors; absence of IV will be handled downstream.
      return undefined;
    }
  };

  // If no browser environment (SSR/test), rely solely on storage
  if (typeof window === "undefined" || typeof window.location === "undefined") {
    __cachedIVParam = readFromStorage();
    return __cachedIVParam;
  }

  let iv: string | undefined;
  try {
    const params = new URLSearchParams(window.location.search);
    iv = params.get("IV") ?? params.get("iv") ?? undefined;
  } catch {
    // Ignore malformed location.search; will fallback to storage instead.
  }

  if (!iv) {
    iv = readFromStorage();
    __cachedIVParam = iv;
    return iv;
  }

  // Persist using unified helper (always ensures iv key)
  try {
    persistUserData({});
  } catch {
    /* ignore persistence during initial IV extraction */
  }

  __cachedIVParam = iv;
  return iv;
};

// Unified persistence helper: merges existing user-data with a patch and ALWAYS ensures the iv key
type UserDataShape = Record<string, unknown>;
const persistUserData = (patch: UserDataShape) => {
  try {
    const existingRaw = localStorage.getItem("user-data");
    let existing: UserDataShape = {};
    if (existingRaw) {
      try {
        const parsed = JSON.parse(existingRaw);
        if (parsed && typeof parsed === "object")
          existing = parsed as UserDataShape;
      } catch {
        /* ignore parse of existing user-data; continue with empty object */
      }
    }
    const currentIV = extractIVFromLocation();
    const merged: UserDataShape = { ...existing, ...patch };
    // Always enforce lowercase iv; prefer current extracted, fallback to any existing casing
    const maybeUpper = (merged as Record<string, unknown>).IV;
    if (currentIV) merged.iv = currentIV;
    else if (maybeUpper && !merged.iv) merged.iv = maybeUpper;
    const loginURL = new URL(globalThis.location.href);
    if (!merged?.keywords) {
      merged.loginURL = loginURL;
    }
    localStorage.setItem("user-data", JSON.stringify(merged));
  } catch {
    /* ignore persistence failures; non-critical */
  }
};

// Module-scope endpoint helpers to avoid useCallback dependency noise
// Encode dynamic path/query components to prevent any potential injection & satisfy static analysis.
const safeEncode = (val: string | undefined) => encodeURIComponent(val ?? "");
const ROOT_ENDPOINT = "/" as const;
const metroEndpoint = (docId: string) =>
  `agent/data/metro/${safeEncode(docId)}/?IV=${safeEncode(
    extractIVFromLocation() ?? ""
  )}`;
const fieldConfigEndpoint = () => `agent/config`;
const VERIFY_TOKEN_ENDPOINT = `agent/data/verify-token?IV=${safeEncode(
  extractIVFromLocation() ?? ""
)}` as const;
const agentDataEndpoint = (docId: string) =>
  `agent/data/${safeEncode(docId)}/?IV=${safeEncode(
    extractIVFromLocation() ?? ""
  )}`;
const generatePacketEndpoint = (docId: string) =>
  `agent/data/onbase-update/${safeEncode(docId)}/?IV=${safeEncode(
    extractIVFromLocation() ?? ""
  )}`;
const generatePacketStatusEndpoint = (docId: string) =>
  `agent/data/check-status/${safeEncode(docId)}/?IV=${safeEncode(
    extractIVFromLocation() ?? ""
  )}`;

type VerifyPinPayload = { docId: string; pin: string };

// Derive field id arrays from form mappers to avoid drift with UI definitions
// (Removed unused intermediate types that triggered lint warnings)
const deriveFieldIds = (metroConfig: unknown) => {
  try {
    // Cast to expected mapper input shape (library mappers perform their own internal validation)
    const mc = metroConfig as Parameters<
      typeof buildPersonalDemographicFormMapper
    >[0];
    const extractLabels = (sections: unknown): string[] => {
      if (!Array.isArray(sections)) return [];
      const out: string[] = [];
      sections.forEach((section) => {
        if (!section || typeof section !== "object") return;
        const fields = (section as { fields?: unknown }).fields;
        if (!Array.isArray(fields)) return;
        fields.forEach((field) => {
          const label = (field as { label?: unknown })?.label;
          if (typeof label === "string" && label.trim()) {
            out.push(label.trim());
          }
        });
      });
      return out;
    };

    const pd = buildPersonalDemographicFormMapper(mc).flatMap((sec) =>
      sec.fields.map((f) => f.label)
    );
    const ra = buildResidentialAddressFormMapper(mc).flatMap((sec) =>
      sec.fields.map((f) => f.label)
    );
    const le = buildLicenseEmergencyContactFormMapper(mc).flatMap((sec) =>
      sec.fields.map((f) => f.label)
    );
    const mnBuilders = [
      buildIndividualResidentLicenseApplicationCriminalHistoryMapper,
      buildIndividualResidentLicenseApplicationLicenseRegistrationIssuesMapper,
      buildIndividualResidentLicenseApplicationJudgmentsBankruptcyMapper,
      buildIndividualResidentLicenseApplicationDelinquentTaxesMapper,
      buildIndividualResidentLicenseApplicationLawsuitsArbitrationMapper,
      buildIndividualResidentLicenseApplicationChildSupportObligationsMapper,
      buildIndividualResidentLicenseApplicationLicensingOtherStatesMapper,
      buildIndividualResidentLicenseApplicationCompanyOwnershipMapper,
      buildIndividualResidentLicenseApplicationUnclaimedPropertyMapper,
    ] as const;
    const mn = mnBuilders.flatMap((builder) => {
      try {
        return extractLabels(builder(mc));
      } catch {
        // Mapper may throw if metro config lacks expected structure; treat as empty.
        return [] as string[];
      }
    });

    const cb = buildUniversalCaresDonationFormMapper(mc).flatMap((sec) =>
      sec.fields.map((f) => f.label)
    );
    return {
      personal: pd,
      residential: ra,
      license: le,
      minnesota: mn,
      cbCaresDonation: cb,
    };
  } catch {
    // Defensive: malformed metro config structure.
    return {
      personal: [],
      residential: [],
      license: [],
      minnesota: [],
    };
  }
};

// Build fast index: keywordId -> fieldid (and field config ref) for one execution
interface RawFieldConfig {
  keywordId?: unknown;
  fieldid?: unknown;
}
const buildKeywordIdIndex = (
  fieldConfig: unknown[]
): Record<string, { fieldid: string }> => {
  const idx: Record<string, { fieldid: string }> = {};
  if (!Array.isArray(fieldConfig)) return idx;
  for (const f of fieldConfig) {
    if (!f || typeof f !== "object") continue;
    const anyF = f as RawFieldConfig;
    const kid = anyF.keywordId;
    if (kid == null) continue;
    const fid = anyF.fieldid;
    if (typeof fid !== "string") continue;
    if (typeof kid !== "string" && typeof kid !== "number") continue;
    const k = String(kid);
    if (!(k in idx)) idx[k] = { fieldid: fid };
  }
  return idx;
};

// Build snapshot object from keywords + fieldConfig
interface RawKeyword {
  keywordId?: unknown;
  keywordStringValue?: unknown;
}
const buildSnapshotFromKeywords = (
  fieldConfig: unknown,
  keywords: unknown[],
  metroConfig?: unknown,
  repeaterKeywords?: unknown[]
) => {
  const snapshot = {
    cbCaresDonation: {} as Record<string, string>,
    personalDemographic: {} as Record<string, string>,
    residentialAddress: {} as Record<string, string>,
    licenseEmergencyContact: {} as Record<string, string>,
    uploadDocuments: {} as Record<string, string>,
    minnesotaQuestions: {} as Record<string, string>,
    minnesotaQuestionFiles: {} as MinnesotaAttachmentFileMap,
  };
  if (
    !Array.isArray(fieldConfig) ||
    !Array.isArray(keywords) ||
    keywords.length === 0
  )
    return snapshot;
  const idIndex = buildKeywordIdIndex(fieldConfig);
  // Pre-build quick membership sets for O(1) classification
  const derived = deriveFieldIds(metroConfig);
  const pdSet = new Set(derived.personal);
  const raSet = new Set(derived.residential);
  const leSet = new Set(derived.license);
  const mnSet = new Set(derived.minnesota);
  const cbSet = new Set(derived.cbCaresDonation);

  const assign = (fid: string, stringVal: string) => {
    if (pdSet.has(fid)) snapshot.personalDemographic[fid] = stringVal;
    else if (raSet.has(fid)) snapshot.residentialAddress[fid] = stringVal;
    else if (leSet.has(fid)) snapshot.licenseEmergencyContact[fid] = stringVal;
    else if (mnSet.has(fid)) snapshot.minnesotaQuestions[fid] = stringVal;
    else if (cbSet.has(fid)) snapshot.cbCaresDonation[fid] = stringVal;
  };
  for (const kw of keywords) {
    if (!kw || typeof kw !== "object") continue;
    const anyKw = kw as RawKeyword;
    const kid = anyKw.keywordId;
    if (kid == null) continue;
    if (typeof kid !== "string" && typeof kid !== "number") continue;
    const entry = idIndex[String(kid)];
    if (!entry) continue; // keywordId not mapped to a known fieldid
    const fid = entry.fieldid;
    const val = anyKw.keywordStringValue;
    if (
      val == null ||
      val === "" ||
      (typeof val !== "string" && typeof val !== "number")
    )
      continue;
    const stringVal = String(val);
    assign(fid, stringVal);
  }

  // Process repeaterKeywords for Q8 (company ownership) and Q7 (licensure states) if provided
  if (Array.isArray(repeaterKeywords)) {
    repeaterKeywords.forEach((repeater) => {
      if (!repeater || typeof repeater !== "object") return;
      const rep = repeater as RepeaterEntry;
      if (rep.repeaterId === Q8_REPEATER_ID) {
        const indexedFields = parseQ8RepeaterKeywords(rep, fieldConfig);
        Object.entries(indexedFields).forEach(([key, value]) => {
          if (typeof value === "string") {
            snapshot.minnesotaQuestions[key] = value;
          }
        });
      } else if (rep.repeaterId === Q7_REPEATER_ID) {
        const q7Fields = parseQ7RepeaterKeywords(rep, fieldConfig);
        Object.entries(q7Fields).forEach(([key, value]) => {
          if (typeof value === 'string') {
            snapshot.minnesotaQuestions[key] = value;
          }
        });
      }
    });
  }

  return snapshot;
};

const collectMinnesotaMapsFrom = (
  candidate: unknown
): MinnesotaAttachmentFileMap[] => {
  if (!candidate || typeof candidate !== "object") return [];
  const record = candidate as Record<string, unknown>;
  const sources: unknown[] = [
    record.minnesotaQuestionFiles,
    record.uploadedMinnesotaFiles,
    record.uploadedFilesMinnesota,
  ];

  // Check uploadedFiles.minnesota
  const uploadedFiles = record.uploadedFiles;
  if (uploadedFiles && typeof uploadedFiles === "object") {
    sources.push((uploadedFiles as Record<string, unknown>).minnesota);
  }

  // Check nested snapshot
  const snapshotNode = record.__globalFormDataSnapshot;
  if (snapshotNode && typeof snapshotNode === "object") {
    sources.push(
      (snapshotNode as Record<string, unknown>).minnesotaQuestionFiles
    );
  }

  // Check API response uploadedFiles array format
  if (Array.isArray(record.uploadedFiles)) {
    const minnesotaFiles = record.uploadedFiles.filter(
      (file) =>
        file &&
        typeof file === "object" &&
        file.docType === "ICR - Miscellaneous" &&
        file.docTypeKW
    );
    if (minnesotaFiles.length > 0) {
      sources.push(minnesotaFiles);
    }
  }

  return sources
    .map((src) => buildMinnesotaFileMapFromStored(src))
    .filter(
      (map) => map && typeof map === "object" && Object.keys(map).length > 0
    );
};

const useOnbaseApi = () => {
  const getData = useCallback(async (): Promise<ApiResponse> => {
    try {
      const { data } = await axiosClient.get(ROOT_ENDPOINT);
      return { data: data?.data, message: data?.message };
    } catch (e) {
      throw toApiRequestError(e);
    }
  }, []);

  const getMetroConfig = useCallback(
    async (docId: string): Promise<ApiResponse> => {
      try {
        const { data } = await axiosClient.get(metroEndpoint(docId));
        const body =
          (data && typeof data === "object"
            ? (data as Record<string, unknown>)["body"]
            : undefined) ?? data;
        const candidateData = body?.data ?? body?.config ?? body?.formconf;
        const finalData = candidateData !== undefined ? candidateData : body;
        const message =
          body?.message ??
          (data && typeof data === "object"
            ? (data as Record<string, unknown>)["message"]
            : undefined);
        if (finalData === undefined) {
          console.warn(
            "getMetroConfig: configuration payload missing expected data field for docId",
            docId,
            data
          );
        }
        return { data: finalData, message };
      } catch (e) {
        throw toApiRequestError(e);
      }
    },
    []
  );

  // Single-flight + localStorage cache for field configuration.
  // Use a memoized object to provide stable identity across renders.
  const fieldConfigPromiseRef = useMemo<{ current?: Promise<ApiResponse> }>(
    () => ({ current: undefined }),
    []
  );

  const getFieldConfig = useCallback(async (): Promise<ApiResponse> => {
    // First attempt to hydrate from localStorage
    try {
      const cached = localStorage.getItem("field-config-cache");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === "object" && "data" in parsed) {
          return {
            data: (parsed as Record<string, unknown>)["data"],
            message: "cached",
          };
        }
      }
    } catch (e) {
      // Intentionally ignore local cache read errors
      if (
        typeof process !== "undefined" &&
        process.env &&
        process.env.NODE_ENV !== "production"
      ) {
        console.debug("getFieldConfig cache read failed", e);
      }
    }

    if (fieldConfigPromiseRef.current) {
      return fieldConfigPromiseRef.current; // in-flight
    }
    fieldConfigPromiseRef.current = (async () => {
      try {
        const { data } = await axiosClient.get(fieldConfigEndpoint());
        const rawData = data?.data;
        const arr: unknown[] = Array.isArray(rawData) ? rawData : [];
        const first =
          arr[0] && typeof arr[0] === "object"
            ? (arr[0] as Record<string, unknown>)
            : {};
        const formconf = first["formconf" as keyof typeof first];
        const resp: ApiResponse = { data: formconf, message: data?.message };
        try {
          localStorage.setItem(
            "field-config-cache",
            JSON.stringify({ data: formconf })
          );
        } catch (e) {
          if (
            typeof process !== "undefined" &&
            process.env &&
            process.env.NODE_ENV !== "production"
          ) {
            console.debug("getFieldConfig cache write failed", e);
          }
        }
        return resp;
      } catch (e) {
        fieldConfigPromiseRef.current = undefined; // allow retry
        throw toApiRequestError(e);
      }
    })();
    return fieldConfigPromiseRef.current;
  }, [fieldConfigPromiseRef]); // memoized ref – safe to include

  const generatePacketStatus = useCallback(
    async (docId: string): Promise<ApiResponse<PacketGenerationStatus>> => {
      try {
        // Use the dedicated status endpoint (previously pointed to generatePacketEndpoint by mistake)
        const { data } = await axiosClient.get(
          generatePacketStatusEndpoint(docId)
        );
        const statusData = data?.data as PacketGenerationStatus | undefined;
        return {
          data: statusData,
          message: data?.message,
          success: data?.success,
        };
      } catch (e) {
        throw toApiRequestError(e);
      }
    },
    []
  );

  // Helper function to process Minnesota files and metadata
  const processMinnesotaFiles = useCallback(
    (
      snapshot:
        | { minnesotaQuestionFiles?: MinnesotaAttachmentFileMap }
        | Record<string, unknown>,
      toStore: Record<string, unknown>,
      metroConfig: unknown
    ) => {
      const nestedData = (toStore as { data?: unknown }).data;
      const candidates: unknown[] = [snapshot, toStore, nestedData];

      const combinedMinnesotaFiles = mergeMinnesotaFileMaps(
        (snapshot as { minnesotaQuestionFiles?: MinnesotaAttachmentFileMap })
          ?.minnesotaQuestionFiles,
        ...candidates.flatMap((candidate) =>
          collectMinnesotaMapsFrom(candidate)
        )
      );

      const resolvedMinnesotaFiles: MinnesotaAttachmentFileMap =
        Object.keys(combinedMinnesotaFiles).length > 0
          ? combinedMinnesotaFiles
          : ({} as MinnesotaAttachmentFileMap);

      // Enrich Minnesota files with metro config metadata
      let enrichedMinnesotaFiles = resolvedMinnesotaFiles;
      if (Object.keys(resolvedMinnesotaFiles).length > 0 && metroConfig) {
        try {
          enrichedMinnesotaFiles = enrichMinnesotaFilesWithMetroConfig(
            resolvedMinnesotaFiles,
            metroConfig
          );
        } catch (error) {
          // Non-fatal; keep original Minnesota files.
          console.warn(
            "Failed to enrich Minnesota files with metro config:",
            error
          );
          enrichedMinnesotaFiles = resolvedMinnesotaFiles;
        }
      }

      return enrichedMinnesotaFiles;
    },
    []
  );

  // Helper function to assign Minnesota uploads to target objects
  const assignMinnesotaData = useCallback(
    (target: unknown, enrichedMinnesotaFiles: MinnesotaAttachmentFileMap) => {
      if (!target || typeof target !== "object") return;
      const record = target as Record<string, unknown>;

      // Preserve hierarchical structure directly on record for consumers needing snapshot-like access
      record.minnesotaQuestionFiles = enrichedMinnesotaFiles;

      const serializedMinnesota = serializeMinnesotaFileMap(
        enrichedMinnesotaFiles
      );

      if (serializedMinnesota.length > 0) {
        // Store serialized form for API consumption
        record.uploadedMinnesotaFiles = serializedMinnesota;
        // Ensure uploadedFiles.minneapolis (legacy naming) remains accessible under uploadedFiles.minnesota
        if (!record.uploadedFiles || typeof record.uploadedFiles !== "object") {
          record.uploadedFiles = {};
        }
        (record.uploadedFiles as Record<string, unknown>).minnesota =
          enrichedMinnesotaFiles;

        // Legacy compatibility: provide uploadedFilesMinnesota as first-file map (questionId->slotKey->first file)
        const legacyFlat: Record<string, unknown> = {};
        Object.entries(enrichedMinnesotaFiles).forEach(([qId, slotMap]) => {
          const flatSlots: Record<string, unknown> = {};
          Object.entries(slotMap).forEach(([slotKey, arr]) => {
            if (Array.isArray(arr) && arr.length > 0) {
              flatSlots[slotKey] = arr[0];
            }
          });
          legacyFlat[qId] = flatSlots;
        });
        record.uploadedFilesMinnesota = legacyFlat;
      } else {
        delete record.uploadedMinnesotaFiles;
        delete record.uploadedFilesMinnesota;
        if (record.uploadedFiles && typeof record.uploadedFiles === "object") {
          delete (record.uploadedFiles as Record<string, unknown>).minnesota;
        }
      }

      // Remove obsolete questions map if present (answers tracked elsewhere)
      delete record.minnesotaQuestions;
    },
    []
  );

  // Helper function to handle snapshot enrichment
  // Helper: safely hydrate Q8 repeater derived fields (company ownership history)
  const hydrateQ8Repeater = (
    snapshot: { minnesotaQuestions?: Record<string, string> },
    repeaterKeywords: unknown[] | undefined,
    fieldConfig: unknown[]
  ) => {
    try {
      if (!Array.isArray(repeaterKeywords)) return;
      const q8Repeater = repeaterKeywords.find(
        (r): r is RepeaterEntry =>
          !!r &&
          typeof r === "object" &&
          (r as RepeaterEntry).repeaterId === "265"
      );
      if (
        !q8Repeater ||
        !Array.isArray(q8Repeater.repeaterKeywords) ||
        !q8Repeater.repeaterKeywords.length
      )
        return;

      type FCEntry = {
        fieldid?: string;
        keywordName?: string;
        displayLabel?: string;
      };
      const fcArr = fieldConfig as FCEntry[];
      const companyField = fcArr.find(
        (f) => f.fieldid === "priorrealestatecompanyname245"
      );
      const licenseField = fcArr.find(
        (f) => f.fieldid === "priorrealestatelicensenumber246"
      );
      const companyLabel =
        companyField?.keywordName ??
        companyField?.displayLabel ??
        "Prior Real Estate Company Name";
      const licenseLabel =
        licenseField?.keywordName ??
        licenseField?.displayLabel ??
        "Prior Real Estate License Number";

      const existing = snapshot.minnesotaQuestions ?? {};
      const additions: Record<string, string> = {};

      (q8Repeater.repeaterKeywords as Record<string, string>[]).forEach(
        (row, idx) => {
          const suffix = `_${idx}`;
          const cVal = row[companyLabel];
          const lVal = row[licenseLabel];
          if (cVal && !(`priorrealestatecompanyname245${suffix}` in existing)) {
            additions[`priorrealestatecompanyname245${suffix}`] = cVal;
          }
          if (
            lVal &&
            !(`priorrealestatelicensenumber246${suffix}` in existing)
          ) {
            additions[`priorrealestatelicensenumber246${suffix}`] = lVal;
          }
        }
      );

      if (Object.keys(additions).length) {
        snapshot.minnesotaQuestions = { ...existing, ...additions };
      } else {
        snapshot.minnesotaQuestions = existing;
      }
    } catch {
      // Swallow hydration errors; repeater-derived fields are optional enhancements.
    }
  };

  // Helper: apply snapshot + Minnesota file data to root and nested data objects
  const applySnapshotToTargets = (
    snapshot: Record<string, unknown>,
    toStore: Record<string, unknown>,
    enrichedMinnesotaFiles: MinnesotaAttachmentFileMap,
    assignMinnesotaDataFn: (
      target: unknown,
      enrichedMinnesotaFiles: MinnesotaAttachmentFileMap
    ) => void
  ) => {
    toStore.__globalFormDataSnapshot = snapshot;
    assignMinnesotaDataFn(toStore, enrichedMinnesotaFiles);

    // Also set uploadDocumentsSections at root level for component access
    if (snapshot.uploadDocumentsSections) {
      toStore.uploadDocumentsSections = snapshot.uploadDocumentsSections;
    }

    const nestedData = toStore.data;
    if (nestedData && typeof nestedData === "object") {
      (nestedData as Record<string, unknown>).__globalFormDataSnapshot =
        snapshot;
      assignMinnesotaDataFn(nestedData, enrichedMinnesotaFiles);

      // Also set at nested level
      if (snapshot.uploadDocumentsSections) {
        (nestedData as Record<string, unknown>).uploadDocumentsSections =
          snapshot.uploadDocumentsSections;
      }
    }
  };

  // Helper: Extract uploads config from metro config
  const extractUploadsConfig = (
    metroConfig: unknown
  ): Array<{ docType?: string }> | undefined => {
    type MetroConfigType = {
      configs?: { Page6Body?: { Uploads?: Array<{ docType?: string }> } };
      Page6Body?: { Uploads?: Array<{ docType?: string }> };
    };

    const mc = metroConfig as MetroConfigType;

    if (Array.isArray(mc?.configs?.Page6Body?.Uploads)) {
      return mc.configs.Page6Body.Uploads;
    }
    if (Array.isArray(mc?.Page6Body?.Uploads)) {
      return mc.Page6Body.Uploads;
    }

    console.warn("No uploads config found in metroConfig");
    return undefined;
  };

  // Helper: Classify document type to section index
  const classifyDocTypeToSection = (
    docType: string,
    docTypeToIndex: Record<string, number>,
    uploadsConfig: Array<{ docType?: string }> | undefined
  ): number => {
    if (docType in docTypeToIndex) {
      return docTypeToIndex[docType];
    }

    // Fallback classification for unknown docTypes
    if (
      docType.includes("Real Estate License") ||
      docType.includes("IC Agreement")
    )
      return 0;
    if (docType.includes("Auto Insurance")) return 1;
    if (
      docType.includes("Proof of Legal Presence") ||
      docType.includes("Birth Certificate") ||
      docType.includes("US Passport") ||
      docType.includes("INS Form")
    )
      return 2;
    if (
      docType.includes("E&O Insurance") ||
      docType.includes("Errors and Omissions")
    )
      return 3;

    // Assign to first available section or create new one
    const maxIndex = Math.max(...Object.values(docTypeToIndex), -1);
    const sectionIndex = maxIndex + 1;
    return uploadsConfig && sectionIndex < uploadsConfig.length
      ? sectionIndex
      : 0;
  };

  // Helper: Process single upload entry
  const processSingleUploadEntry = useCallback(
    (
      entry: Record<string, unknown>,
      allowedPairs: Array<{ docType: string; misc: string }>,
      docTypeToIndex: Record<string, number>,
      uploadsConfig: Array<{ docType?: string }> | undefined
    ): {
      sectionIndex: number;
      processedEntry: Record<string, unknown>;
    } | null => {
      if (!entry || typeof entry !== "object") return null;

      const getDocTypeKW = (e: Record<string, unknown>) => {
        const raw = e.docTypeKW;
        return typeof raw === "string" ? raw.trim() : "";
      };

      const getDocType = (e: Record<string, unknown>) => {
        const raw = e.docType;
        return typeof raw === "string" ? raw.trim() : "";
      };

      const extractMiscKW = (e: Record<string, unknown>) => {
        const miscA = (e as { MiscDocTypeKW?: string }).MiscDocTypeKW;
        const miscB = (e as { miscDocTypeKW?: string }).miscDocTypeKW;
        if (typeof miscA === "string" && miscA.trim()) return miscA.trim();
        if (typeof miscB === "string" && miscB.trim()) return miscB.trim();
        return "";
      };

      const docTypeKW = getDocTypeKW(entry);
      if (docTypeKW) return null;
      const docType = getDocType(entry);
      if (!docType) return null;
      const miscDocTypeKW = extractMiscKW(entry);

      if (allowedPairs.length) {
        const whitelisted = allowedPairs.some(
          (p) =>
            p.docType === docType && (p.misc === "" || p.misc === miscDocTypeKW)
        );
        if (!whitelisted) return null;
      }

      const sectionIndex = classifyDocTypeToSection(
        docType,
        docTypeToIndex,
        uploadsConfig
      );

      const fullFileName =
        typeof entry.fileName === "string" ? entry.fileName : "";
      const displayName = fullFileName.includes("/")
        ? fullFileName.split("/").pop() ?? fullFileName
        : fullFileName;

      const buildId = (
        e: Record<string, unknown>,
        sectionIdx: number
      ): string => {
        const idA = (e as { _id?: unknown })._id;
        if (typeof idA === "string") return idA;
        const idB = (e as { id?: unknown }).id;
        if (typeof idB === "string") return idB;
        if (typeof crypto !== "undefined" && crypto.randomUUID)
          return crypto.randomUUID();
        // Fallback: build a sufficiently unique id using cryptographic randomness when possible
        try {
          if (typeof crypto !== "undefined" && crypto.getRandomValues) {
            const buf = new Uint32Array(2);
            crypto.getRandomValues(buf);
            return `${sectionIdx}-${buf[0].toString(36)}-${buf[1]
              .toString(36)
              .slice(0, 6)}`;
          }
        } catch {
          /* ignore and fallback further */
        }
        // Last resort (very unlikely in modern environments): time-based only
        return `${sectionIdx}-${Date.now().toString(36)}`;
      };

      const processedEntry: Record<string, unknown> = {
        id: buildId(entry, sectionIndex),
        name: displayName,
        size: typeof entry.size === "number" ? entry.size : 0,
        type: "application/pdf",
        isUploaded: true,
        remoteKey: fullFileName,
        docType,
        miscDocTypeKW:
          docTypeKW ||
          ((entry as { miscDocTypeKW?: string }).miscDocTypeKW ??
          ""),
        section: String(sectionIndex),
      };

      return { sectionIndex, processedEntry };
    },
    []
  );

  // Helper: Create snapshot maps from sections data
  const createSnapshotMaps = (sectionsData: Record<string, unknown>) => {
    const uploadDocuments: Record<string, string> = {};
    const uploadDocumentsSections: Record<string, Record<string, string>> = {};
    const uploadDocumentsRemoteKeys: Record<string, string> = {};

    Object.entries(sectionsData).forEach(([sectionKey, files]) => {
      if (!Array.isArray(files)) return;

      const sectionMap: Record<string, string> = {};
      files.forEach((file) => {
        if (!file || typeof file !== "object") return;

        const f = file as Record<string, unknown>;
        const id = typeof f.id === "string" ? f.id : "";
        const name = typeof f.name === "string" ? f.name : "";
        const remoteKey = typeof f.remoteKey === "string" ? f.remoteKey : "";

        if (id && name) {
          uploadDocuments[id] = name;
          sectionMap[id] = name;
          if (remoteKey) uploadDocumentsRemoteKeys[id] = remoteKey;
        }
      });

      if (Object.keys(sectionMap).length > 0) {
        uploadDocumentsSections[sectionKey] = sectionMap;
      }
    });

    return {
      uploadDocuments,
      uploadDocumentsSections,
      uploadDocumentsRemoteKeys,
    };
  };

  // Handle mapping of uploadedFiles from API to dynamic sections structure
  const handleUploadedFilesMapping = useCallback(
    async (
      toStore: Record<string, unknown>,
      metroConfig: unknown,
      snapshot: Record<string, unknown>
    ) => {
      try {
        const uploadsConfig = extractUploadsConfig(metroConfig);
        const rawUploads = toStore?.uploadedFiles;

        if (Array.isArray(rawUploads)) {
          // Create docType to section index mapping
          const docTypeToIndex: Record<string, number> = {};
          uploadsConfig?.forEach((item, idx) => {
            const dt =
              typeof item.docType === "string" ? item.docType.trim() : "";
            if (dt) docTypeToIndex[dt] = idx;
          });

          // Build allowed docType + misc pairs
          const allowedPairs = (uploadsConfig ?? [])
            .map((u) => {
              const docType =
                typeof u.docType === "string" ? u.docType.trim() : "";
              const miscKW =
                (u as { MiscDocTypeKW?: string; miscDocTypeKW?: string })
                  .MiscDocTypeKW ??
                (u as { miscDocTypeKW?: string }).miscDocTypeKW;
              const misc = typeof miscKW === "string" ? miscKW.trim() : "";
              return { docType, misc };
            })
            .filter((p) => p.docType);

          const sections: Record<string, Record<string, unknown>[]> = {};

          // Process each upload entry
          rawUploads.forEach((raw) => {
            const result = processSingleUploadEntry(
              raw as Record<string, unknown>,
              allowedPairs,
              docTypeToIndex,
              uploadsConfig
            );
            if (result) {
              const { sectionIndex, processedEntry } = result;
              const sectionKey = String(sectionIndex);
              if (!sections[sectionKey]) sections[sectionKey] = [];
              sections[sectionKey].push(processedEntry);
            } else {
              toStore.uploadedFiles = { sections: {} }; // Clear invalid uploads
            }
          });

          // Update toStore with structured format
          if (Object.keys(sections).length > 0) {
            toStore.uploadedFiles = { sections };
          }
        }

        // Create snapshot maps for UI consumption
        const uploadedFiles = toStore.uploadedFiles;
        if (
          uploadedFiles &&
          typeof uploadedFiles === "object" &&
          !Array.isArray(uploadedFiles)
        ) {
          const structured = uploadedFiles as Record<string, unknown>;
          const sectionsData =
            structured.sections && typeof structured.sections === "object"
              ? (structured.sections as Record<string, unknown>)
              : {};

          const maps = createSnapshotMaps(sectionsData);
          snapshot.uploadDocuments = maps.uploadDocuments;
          snapshot.uploadDocumentsSections = maps.uploadDocumentsSections;
          snapshot.uploadDocumentsRemoteKeys = maps.uploadDocumentsRemoteKeys;
        } else {
          // Ensure empty objects for consistency
          snapshot.uploadDocuments = {};
          snapshot.uploadDocumentsSections = {};
          snapshot.uploadDocumentsRemoteKeys = {};
        }
      } catch (error) {
        // Non-fatal: keep snapshot upload sections empty if mapping fails.
        console.warn("handleUploadedFilesMapping failed:", error);
        // Ensure empty objects even on error
        snapshot.uploadDocuments = {};
        snapshot.uploadDocumentsSections = {};
        snapshot.uploadDocumentsRemoteKeys = {};
      }
    },
    [processSingleUploadEntry]
  );

  const enrichSnapshot = useCallback(
    async (toStore: Record<string, unknown>, docId: string) => {
      try {
        const keywords = toStore.keywords;
        // Do NOT early-return purely because keywords are absent; we still need to process uploadedFiles mapping
        const safeKeywords = Array.isArray(keywords) ? keywords : [];

        const repeaterKeywords = Array.isArray(toStore.repeaterKeywords)
          ? toStore.repeaterKeywords
          : undefined;

        // Fetch configs in parallel (best effort)
        const [fieldCfgResp, metroCfgResp] = await Promise.all([
          getFieldConfig().catch(() => undefined),
          getMetroConfig(docId).catch(() => undefined),
        ]);

        const fieldConfig = Array.isArray(fieldCfgResp?.data)
          ? (fieldCfgResp.data as unknown[])
          : undefined;
        if (!fieldConfig) return;

        const metroConfig = metroCfgResp?.data;

        // Build base snapshot
        const snapshot = buildSnapshotFromKeywords(
          fieldConfig,
          safeKeywords,
          metroConfig,
          repeaterKeywords
        );

        // Minnesota files processing
        const enrichedMinnesotaFiles = processMinnesotaFiles(
          snapshot,
          toStore,
          metroConfig
        );
        snapshot.minnesotaQuestionFiles = enrichedMinnesotaFiles;

        // Hydrate Q8 repeater derived fields
        hydrateQ8Repeater(snapshot, repeaterKeywords, fieldConfig);

        // Dynamic uploads mapping: handle uploadedFiles from API response
        await handleUploadedFilesMapping(toStore, metroConfig, snapshot);

        // Apply snapshot to targets
        applySnapshotToTargets(
          snapshot as Record<string, unknown>,
          toStore,
          enrichedMinnesotaFiles,
          assignMinnesotaData
        );
      } catch (e) {
        if (
          typeof process !== "undefined" &&
          process.env &&
          process.env.NODE_ENV !== "production"
        ) {
          console.debug("enrichSnapshot failed", e);
        }
      }
    },
    [
      getFieldConfig,
      getMetroConfig,
      processMinnesotaFiles,
      assignMinnesotaData,
      handleUploadedFilesMapping,
    ]
  );

  // Helper: build the unified store object from verifyPin response payload
  const buildVerifyPinStore = (
    payload: VerifyPinPayload,
    data: unknown
  ): { [key: string]: unknown } => {
    const bodyData = (data as { body?: { data?: unknown } })?.body?.data;
    const legacyData = (data as { data?: unknown })?.data;
    const docId = payload.docId ?? (bodyData as { docId?: string })?.docId ?? (legacyData as { docId?: string })?.docId;
    return {
      ...(typeof legacyData === "object" && legacyData ? legacyData : {}),
      ...(typeof bodyData === "object" && bodyData ? bodyData : {}),
      docId,
    };
  };

  // Helper: enrich snapshot + packet status
  const enrichAndAttachSnapshot = useCallback(async (
    toStore: Record<string, unknown>,
    docId: string
  ) => {
    await enrichSnapshot(toStore, docId);
    // Fetch latest packet status and merge if present
    try {
      const { data: statusResp } = await generatePacketStatus(docId);
      const agentStatus = statusResp?.agentStatus;
      if (typeof agentStatus === "string" && agentStatus) {
        toStore.packetGenerationStatus = agentStatus;
      }
    } catch (e) {
      // Non-fatal; keep existing toStore
      if (
        typeof process !== "undefined" &&
        process.env &&
        process.env.NODE_ENV !== "production"
      ) {
        console.debug("verifyPin: packet status fetch failed", e);
      }
    }
  }, [enrichSnapshot, generatePacketStatus]);

  // Helper: resolve isReadDoc array from verify response or secondary agent data endpoint
  const resolveServerIsReadDoc = async (
    bodyData: unknown,
    legacyData: unknown,
    docId: string | undefined
  ): Promise<boolean[] | undefined> => {
    let serverIsRead: unknown;
    const bodyDataWithIsReadDoc = bodyData as { isReadDoc?: unknown };
    const legacyDataWithIsReadDoc = legacyData as { isReadDoc?: unknown };
    if (Array.isArray(bodyDataWithIsReadDoc?.isReadDoc)) serverIsRead = bodyDataWithIsReadDoc.isReadDoc;
    else if (Array.isArray(legacyDataWithIsReadDoc?.isReadDoc)) serverIsRead = legacyDataWithIsReadDoc.isReadDoc;
    if (!serverIsRead && docId) {
      try {
        const agentCfgResp = await axiosClient.get(agentDataEndpoint(docId));
        const cfgData = agentCfgResp?.data?.data;
        if (cfgData && Array.isArray(cfgData.isReadDoc)) {
          serverIsRead = cfgData.isReadDoc;
        }
      } catch {
        /* ignore agent data fetch */
      }
    }
    return Array.isArray(serverIsRead) ? (serverIsRead as boolean[]) : undefined;
  };

  // Helper: merge server isReadDoc with locally stored user-data (logical OR)
  const mergeIsReadDocWithLocal = (serverArr: boolean[]): boolean[] => {
    try {
      const existingRaw = localStorage.getItem("user-data");
      if (existingRaw) {
        const existingParsed = JSON.parse(existingRaw);
        const localArr: unknown = existingParsed?.isReadDoc;
        if (Array.isArray(localArr)) {
          return serverArr.map((v, i) => v === true || localArr[i] === true);
        }
      }
    } catch {
      // fall through to return serverArr
    }
    return serverArr;
  };

  const verifyPin = useCallback(
    async (payload: VerifyPinPayload): Promise<ApiResponse> => {
      try {
        const storedUserData = getUserData()
        const { data } = await axiosClient.post(VERIFY_TOKEN_ENDPOINT, payload);
        const bodyData = data?.body?.data;
        const legacyData = data?.data;
        if (
          storedUserData?.keywords &&
          storedUserData?.documentId !== legacyData?.documentId
        ) {
          clearStaleCaches();
        }
        const toStore = buildVerifyPinStore(payload, data);
        toStore.loginURL = new URL(globalThis.location.href);
        const docId = (toStore.docId as string) || "";

        await enrichAndAttachSnapshot(toStore as Record<string, unknown>, docId);

        // Synchronize isReadDoc from server so logout/login restores state.
        try {
          const serverIsRead = await resolveServerIsReadDoc(bodyData, legacyData, docId);
          if (serverIsRead) {
            toStore.isReadDoc = mergeIsReadDocWithLocal(serverIsRead);
          }
        } catch (e) {
          if (
            typeof process !== "undefined" &&
            process.env &&
            process.env.NODE_ENV !== "production"
          ) {
            console.debug("verifyPin: isReadDoc sync failed", e);
          }
        }

        // Persist user-data (non-fatal on failure)
        try {
          persistUserData(toStore as Record<string, unknown>);
        } catch (e) {
          console.warn("verifyPin: failed to persist user-data", e);
        }
        return { data: bodyData, message: data?.body?.message };
      } catch (e) {
        throw toApiRequestError(e);
      }
    },
    [enrichAndAttachSnapshot]
  );

  const getAgentDataConfig = useCallback(
    async (docId: string): Promise<ApiResponse> => {
      try {
        const { data } = await axiosClient.get(agentDataEndpoint(docId));
        return { data: data?.data, message: data?.message };
      } catch (e) {
        throw toApiRequestError(e);
      }
    },
    []
  );

  const putAgentDataConfig = useCallback(
    async (docId: string, body: unknown): Promise<ApiResponse> => {
      try {
        const { data } = await axiosClient.put(agentDataEndpoint(docId), body);
        return { data: data?.data, message: data?.message };
      } catch (e) {
        throw toApiRequestError(e);
      }
    },
    []
  );

  const generatePacket = useCallback(
    async (docId: string): Promise<ApiResponse> => {
      try {
        const { data } = await axiosClientGeneratePacket.put(
          generatePacketEndpoint(docId)
        );
        return {
          data: data?.data,
          message: data?.message,
          success: data?.success,
        };
      } catch (e) {
        throw toApiRequestError(e);
      }
    },
    []
  );

  const fetchMetroLists = useCallback(
    async (brandName: string): Promise<ApiResponse<MetroContactInfo[]>> => {
      interface RawMetroEntry {
        [key: string]: unknown;
        metroId?: string;
        metroName?: string;
        brandKey?: string;
        supportEmail?: string;
      }
      try {
        console.log("Fetching metro lists for brand:", brandName);
        const { data } = await axiosClient.get<ApiResponse<RawMetroEntry[]>>(
          `/agent/metro/name-lists?brandKey=${brandName}`
        );
        const rawList: unknown = data?.data;
        const metroList: RawMetroEntry[] = Array.isArray(rawList)
          ? rawList
          : [];

        const processedMetroData: MetroContactInfo[] = metroList
          .filter(
            (
              m
            ): m is Required<Pick<RawMetroEntry, "metroId" | "metroName">> &
              RawMetroEntry =>
              typeof m.metroName === "string" &&
              m.metroName.trim() !== "" &&
              typeof m.metroId === "string"
          )
          .map((m) => ({
            metroId: m.metroId,
            metroName: m.metroName,
            brandKey: typeof m.brandKey === "string" ? m.brandKey : "",
            supportEmail:
              typeof m.supportEmail === "string" ? m.supportEmail : "",
            supportbranchmanager:
              typeof m.supportbranchmanager === "string" ? m.supportbranchmanager : "",
          }));

        return {
          data: processedMetroData,
          message: data?.message,
          success: data?.success,
        };
      } catch (e) {
        throw toApiRequestError(e);
      }
    },
    []
  );

  return {
    getData,
    getMetroConfig,
    getFieldConfig,
    verifyPin,
    getAgentDataConfig,
    putAgentDataConfig,
    generatePacket,
    generatePacketStatus,
    fetchMetroLists,
  };
};

export default useOnbaseApi;
